/**
 * Time-of-birth input helpers for the Contacts personal-data editor.
 *
 * REUSE, not reinvent:
 *   · Digit-grouping is the intake `maskTime` (src/lib/intakeMasks.ts) — the same
 *     one-box `HH MM` formatter used across the L1A intake redesign.
 *   · The "a typed dot means a colon" convenience mirrors the astro bulk-entry
 *     `normalizeTob` (av-astro/frontend/src/lib/tob.ts, `14.30` → `14:30`), which
 *     itself mirrors the backend `av_astro.astro.time.normalize_tob`. maskTime
 *     already absorbs the dot (it strips every non-digit), so `17.30`, `1730`
 *     and `17:30` all normalize to the same value.
 *
 * The one deviation from `maskTime`: it renders `HH : MM` (spaced) for the dense
 * intake grid, whereas the Contact `tob` is STORED as a tight `"HH:MM"` string.
 * We tighten the separator here so the field value IS the stored shape — no
 * lossy round-trip on save, and the stored `tob` shape is unchanged.
 */
import { maskTime } from './intakeMasks';

/**
 * PURE — format a raw string as a tight 24-hour `HH:MM`.
 * `17.30` → `17:30`, `1730` → `17:30`, `17:30` → `17:30`. Partial while typing
 * (`1` → `1`, `173` → `17:3`). Reuses `maskTime`, then drops the spaces so the
 * value matches the stored `tob` shape.
 */
export function maskTob(raw: string): string {
  return maskTime(raw).replace(/\s+/g, '');
}

/**
 * PURE — a COMPLETE, valid 24-hour time (`HH:MM`, 00–23 : 00–59)?
 * Empty is treated as "not complete" by the caller (an empty TOB is allowed —
 * it just clears the field). Used to gate save + drive the inline hint.
 */
export function isCompleteTob(v: string): boolean {
  const m = /^(\d{2}):(\d{2})$/.exec(v.trim());
  if (!m) return false;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  return h >= 0 && h <= 23 && mm >= 0 && mm <= 59;
}
